import React, { useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';

import {
    CustomComponentIconKey,
    CustomComponentsIcons,
} from '../../constants/icons';

import useComponentTypesQuery from '../../hooks/component-types/useComponentTypesQuery';

import { ComponentTypeMapped } from '../../models/component-types-model';

import { lightenOrDarkenColor } from './helper';

const primaryIconNames = [
    CustomComponentIconKey.Tada,
    CustomComponentIconKey.HandWave,
    CustomComponentIconKey.Heart,
    CustomComponentIconKey.Cta,
    CustomComponentIconKey.Gift,
    CustomComponentIconKey.Tag,
];

interface ComponentTypesIconWrapperProps {
    dataKey?: string;
    iconName?: string;
    primaryColor?: string;
}

const ComponentTypesIconWrapper: React.FC<ComponentTypesIconWrapperProps> = ({
    dataKey,
    iconName,
    primaryColor = '#000000',
}) => {
    const { data } = useComponentTypesQuery();

    // current component type
    const componentType = useMemo<ComponentTypeMapped | undefined>(
        () =>
            dataKey ? (data?.map?.[dataKey] as ComponentTypeMapped) : undefined,
        [dataKey, data],
    );

    // extracted properties
    const componentTypeIconName = useMemo(
        () => componentType?.icon_name ?? iconName,
        [componentType, iconName],
    );
    const componentTypeBaseColor = useMemo(
        () => componentType?.base_color ?? primaryColor,
        [componentType, primaryColor],
    );
    const componentTypeSecondaryColor = useMemo(
        () =>
            componentType?.secondary_color ??
            lightenOrDarkenColor(componentTypeBaseColor, -10),
        [componentType, componentTypeBaseColor],
    );

    // icon data
    const IconData = useMemo(
        () =>
            CustomComponentsIcons[
                componentTypeIconName as keyof typeof CustomComponentsIcons
            ],
        [componentTypeIconName],
    );

    const IconComponent = IconData?.icon;

    const fillColor = useMemo<string>(() => {
        if (
            primaryIconNames.includes(
                componentTypeIconName as CustomComponentIconKey,
            )
        ) {
            return componentTypeSecondaryColor;
        }
        return componentTypeBaseColor;
    }, [
        componentTypeIconName,
        componentTypeSecondaryColor,
        componentTypeBaseColor,
    ]);

    const strokeColor = useMemo<string>(() => {
        if (
            primaryIconNames.includes(
                componentTypeIconName as CustomComponentIconKey,
            )
        ) {
            return componentTypeBaseColor;
        }
        return componentTypeSecondaryColor;
    }, [
        componentTypeIconName,
        componentTypeBaseColor,
        componentTypeSecondaryColor,
    ]);

    if (!IconComponent) {
        return null;
    }

    // SVG cloning
    const svgString = ReactDOMServer.renderToString(<IconComponent />);

    // Only recolor non-white areas so the icon keeps its original white highlights
    // and looks consistent in both colored and default versions.
    const modifiedSvg = svgString
        .replace(
            /fill="(?!none|white|#fff|#FFFFFF)[^"]*"/g,
            `fill="${fillColor}"`,
        )
        .replace(
            /stroke="(?!none|white|#fff|#FFFFFF)[^"]*"/g,
            `stroke="${strokeColor}"`,
        );

    return (
        <div
            dangerouslySetInnerHTML={{ __html: modifiedSvg }} // Inject modified SVG
            style={{ display: 'inline-flex' }}
        />
    );
};

export default ComponentTypesIconWrapper;
